// Accept and assign — the pure rules behind the record picker in the call
// review drawer (SEP-183; ADR-0015, section "Client Knowledge").
//
// A knowledge item belongs to the PERSON on the telephone. At the thumb, the
// reviewer elects which record or records the insight applies to. The
// candidates are the person's own relationship counterparties, so this module
// needs no new join: the review drawer reads the existing
// `GET /api/relationships?endpointId=<contactId>` endpoint.
//
// Pure on purpose, so the pre-tick rule is unit-tested without a browser.

import {
    RELATIONSHIP_ENDPOINT_TYPE_LABELS,
    SUITABILITY_RECORD_LABEL_SEPARATOR,
    type KnowledgeAssignmentTarget,
    type KnowledgeAssignmentTargetType,
    type RelationshipRow,
} from '@septima/contract';

/** One record the reviewer can tick. */
export interface AssignableRecord {
    targetType: KnowledgeAssignmentTargetType;
    targetId: string;
    /** What the reviewer reads. The tier word comes from the shared contract
     *  map: an `account` reads "Account", and "Client" names
     *  the registered holder alone. */
    label: string;
}

const ASSIGNABLE_TYPES: ReadonlyArray<KnowledgeAssignmentTargetType> = [
    'suitability-record',
    'account',
];

/** Suitability Records read first: they are the mandate the IM works to. */
const TYPE_ORDER: Record<KnowledgeAssignmentTargetType, number> = {
    'suitability-record': 0,
    account: 1,
};

function isAssignableType(value: string): value is KnowledgeAssignmentTargetType {
    return (ASSIGNABLE_TYPES as ReadonlyArray<string>).includes(value);
}

/**
 * One picker row's label. This list is ONE line per record, so the Clients tier
 * cannot split the person from the account code the way a relationship cell
 * does (SEP-188). The person leads, and the code follows on the same line.
 *
 * Dropping the code here would be wrong rather than merely terser: one person
 * can hold two Accounts, and two identically-labelled tick boxes are exactly
 * the ambiguity {@link preTickedTargets} refuses to guess through.
 *
 * The `via` marker on the relationships panel composes the same one line on the
 * server (`joinTierLabel` in `relationships.service.ts`), and the two stay
 * separate on purpose. They join DIFFERENT things: the server joins a resolved
 * ontology endpoint to that tier's reference, before the DTO exists, while this
 * function joins a `RelationshipRow` DTO and substitutes the tier word for a
 * name the caller may not read. Only the punctuation is common, and the
 * punctuation is already ONE constant that both sides import
 * (`SUITABILITY_RECORD_LABEL_SEPARATOR`), so its value cannot drift. The ORDER
 * is pinned by a literal on each side: `'Ashgrove · 2620 140229'` in
 * `knowledge-assignment.test.ts`, and `'Marina Ashcombe · SUR-2001'` in the
 * server's `panel-transitive-rows.test.ts`. Change the order on one surface and
 * that surface's test goes red.
 */
function assignableLabel(row: RelationshipRow): string {
    const name = row.counterpartName ?? RELATIONSHIP_ENDPOINT_TYPE_LABELS[row.counterpartType];
    return row.counterpartReference
        ? `${name}${SUITABILITY_RECORD_LABEL_SEPARATOR}${row.counterpartReference}`
        : name;
}

/**
 * The records a Contact can carry an insight onto.
 *
 * Keeps the ACTIVE counterparties only (an ended relationship is history, and
 * a new insight does not belong on it), keeps the two assignable tiers only,
 * and de-duplicates on (type, id) because one person can hold two roles on one
 * record. A counterpart the caller cannot read carries a null name; the tier
 * label then stands in, so the row is never blank.
 */
export function assignableRecordsFrom(rows: ReadonlyArray<RelationshipRow>): AssignableRecord[] {
    const byKey = new Map<string, AssignableRecord>();
    for (const row of rows) {
        if (row.endDate) continue;
        if (!isAssignableType(row.counterpartType)) continue;
        const key = `${row.counterpartType}:${row.counterpartId}`;
        if (byKey.has(key)) continue;
        byKey.set(key, {
            targetType: row.counterpartType,
            targetId: row.counterpartId,
            label: assignableLabel(row),
        });
    }
    return [...byKey.values()].sort(
        (a, b) =>
            TYPE_ORDER[a.targetType] - TYPE_ORDER[b.targetType] ||
            a.label.localeCompare(b.label, 'en-GB'),
    );
}

/**
 * The ADR-0015 default at the thumb.
 *
 * A plain accept stays at Contact level and assigns NOTHING. When the person
 * carries exactly one record, that single unambiguous record is pre-ticked.
 * Two or more records is precisely the case the reporter describes, and the
 * system must not guess between them.
 */
export function preTickedTargets(
    records: ReadonlyArray<AssignableRecord>,
): KnowledgeAssignmentTarget[] {
    if (records.length !== 1) return [];
    return [{ targetType: records[0].targetType, targetId: records[0].targetId }];
}

/** The record selection the drawer keeps for one staged artefact. */
export interface StagedAssignment {
    assignTo?: KnowledgeAssignmentTarget[];
    /** True once the reviewer ticked or unticked a record themself. */
    assignTouched?: boolean;
}

/**
 * The records that a newly staged decision carries.
 *
 * A pre-tick is a SUGGESTION, so it applies only while the reviewer has not
 * answered the question. As soon as the reviewer touches the picker, that
 * answer stands, and it survives a thumb-down and a return to the thumb-up. An
 * empty selection that the reviewer chose is not the same as no selection at
 * all, so the two cases must not both read as "empty".
 */
export function assignmentForDecision(
    previous: StagedAssignment | undefined,
    decision: 'approve' | 'reject',
    records: ReadonlyArray<AssignableRecord>,
): KnowledgeAssignmentTarget[] {
    if (previous?.assignTouched) return [...(previous.assignTo ?? [])];
    return decision === 'approve' ? preTickedTargets(records) : [];
}

/** Is this record ticked in the staged selection? */
export function isTargetSelected(
    selected: ReadonlyArray<KnowledgeAssignmentTarget>,
    record: AssignableRecord,
): boolean {
    return selected.some(
        (t) => t.targetType === record.targetType && t.targetId === record.targetId,
    );
}

/** Tick or untick one record, keeping the selection stable in record order. */
export function toggleTarget(
    selected: ReadonlyArray<KnowledgeAssignmentTarget>,
    record: AssignableRecord,
): KnowledgeAssignmentTarget[] {
    if (isTargetSelected(selected, record)) {
        return selected.filter(
            (t) => !(t.targetType === record.targetType && t.targetId === record.targetId),
        );
    }
    return [...selected, { targetType: record.targetType, targetId: record.targetId }];
}
